import {
  DetailRequirement,
  ProgramBenchmarkName,
  ProgramModel,
  TraitId,
  UserRefModel,
  WorkflowItem,
} from '@cibo/core'
import { useDetailsChanged } from '@cibo/landmanager'
import { Loading, useAuth } from '@cibo/profile'
import AddIcon from '@mui/icons-material/Add'
import { Box, Button, Grid, Stack, Typography } from '@mui/material'
import { GridRowId } from '@mui/x-data-grid-pro'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom'
import { useDeleteWorkflow, useWorkflowManagement } from '../..'
import { useWorkflowCompletion } from '../../hooks'
import { usePaginatedWorkflowFields } from '../../index'
import { useProgram, useWorkflow } from '../../queries'
import { AddInformation, AddInformationNavButtonsProps } from '../AddInformation'
import { useFieldOverlaps, useLedgerConflicts } from '../WorkflowManagement'

interface TraitWorkflowEditorProps
  extends Partial<Pick<AddInformationNavButtonsProps, 'onContinue' | 'onFinishLater'>> {
  workflowId: string
  workflow: WorkflowItem
  refetchWorkflow(): void
}

const WorkflowTraitEditor = ({
  workflowId,
  onContinue,
  onFinishLater,
  workflow,
  refetchWorkflow,
}: TraitWorkflowEditorProps) => {
  const { t } = useTranslation('@cibo/programs/AddWorkflowInformation')
  const auth = useAuth()
  const {
    setAddFieldsModal,
    setRemoveFieldsModal,
    setOverlapWarning,
    setLedgerConflicts,
    setEnrollmentWindowClosed,
  } = useWorkflowManagement()
  const { mutateAsync: deleteWorkflow } = useDeleteWorkflow()
  const navigate = useNavigate()
  const { isLoading: fieldsLoading, data: workflowFieldsData } =
    usePaginatedWorkflowFields(workflowId)
  useDetailsChanged(() => {
    refetchWorkflow()
  })
  const ownedByUser = new UserRefModel(workflow.ownedBy)
  const programQuery = useProgram({ programId: workflow.programId })

  const isLoadingData = !programQuery.data || fieldsLoading

  const [params, setParams] = useSearchParams()
  const traitStatuses = useWorkflowCompletion(workflowId, workflow.programId)

  const fieldOverlaps = useFieldOverlaps(workflowId)
  const ledgerConflicts = useLedgerConflicts(workflowId)

  const [needsOverlapCheck, setNeedsOverlapCheck] = useState(true)
  useEffect(() => {
    if (ledgerConflicts.isFetching || !fieldOverlaps.isReady || !needsOverlapCheck) return

    if (fieldOverlaps.overlaps?.length) {
      setOverlapWarning({ open: true, workflowId })
    } else if (ledgerConflicts.conflicts?.length) {
      setLedgerConflicts({ open: true, workflowId, context: 'enroll' })
    }

    setNeedsOverlapCheck(false)
  }, [fieldOverlaps.isReady, fieldOverlaps.overlaps, ledgerConflicts.isFetching, needsOverlapCheck])

  useEffect(() => {
    if (traitStatuses?.statuses?.length && !params.get('trait')) {
      const firstMissingTrait = traitStatuses.statuses.find(status => status.missing)
      setParams(
        { trait: firstMissingTrait?.traitId || traitStatuses.statuses[0].traitId },
        { replace: true }
      )
    }
  }, [traitStatuses?.isLoading, params])

  useEffect(() => {
    if (programQuery.data && programQuery.data?.enrollmentWindow.enrollmentStatus !== 'open') {
      setEnrollmentWindowClosed({
        open: true,
        workflowId,
        count: workflow.fieldCounts.total,
        programName: programQuery.data?.content.fields.title,
        onDeleteSuccess: () => {
          navigate(-1)
        },
        programId: workflow.programId,
      })
    }
  }, [programQuery?.isFetching, workflow.programId])

  if (!traitStatuses || traitStatuses?.isLoading || isLoadingData) {
    return <Loading />
  }
  if (!workflow) {
    return <Navigate to={'/'} /> // TODO: fix this
  }

  const programModel = programQuery.data && new ProgramModel(programQuery.data)

  const requiredDetails: DetailRequirement[] | undefined =
    programModel &&
    (programModel
      .requiredDetails(workflow.benchmark as ProgramBenchmarkName)
      .map(detail => ({ ...detail, dataType: 'field' })) as DetailRequirement[])

  if (!workflowFieldsData || workflowFieldsData.items?.length === 0) {
    return (
      <Stack spacing={2} direction="row">
        <Button
          variant="outlined"
          color="error"
          size="small"
          onClick={() => deleteWorkflow(workflowId).then(() => navigate(-1))}
        >
          {t('cancel')}
        </Button>

        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          size="small"
          onClick={() => {
            setAddFieldsModal({
              workflowId,
              onSuccess: () => setNeedsOverlapCheck(true),
            })
          }}
        >
          {t('addFields')}
        </Button>
      </Stack>
    )
  }

  const context = workflow && auth && workflow?.ownedBy?.userId !== auth?.userId ? 'grower' : 'self'
  const grower = workflow?.ownedBy?.givenName
    ? `${workflow.ownedBy.givenName} ${workflow.ownedBy.familyName}`
    : workflow.ownedBy.userPrimaryEmail

  if (!requiredDetails) return null

  return (
    <>
      <Grid container>
        <Grid item md={10} sm={12}>
          <Typography>
            {t('description', { count: workflowFieldsData.items?.length, context, grower })}
          </Typography>
        </Grid>
        <Grid item md={2} sm={12} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            startIcon={<AddIcon />}
            size="small"
            variant="contained"
            color="secondary"
            onClick={() =>
              setAddFieldsModal({
                workflowId,
                onSuccess: () => setNeedsOverlapCheck(true),
              })
            }
          >
            {t('addFields')}
          </Button>
        </Grid>
      </Grid>
      <Box>
        <AddInformation
          growerName={ownedByUser.displayNameFull}
          fields={workflowFieldsData.items}
          programIds={[workflow.programId]}
          benchmark={workflow.benchmark}
          isLoadingData={isLoadingData}
          years={
            params.get('trait')
              ? programModel?.yearsForTrait(
                  workflow.benchmark as ProgramBenchmarkName,
                  params.get('trait') as TraitId
                )
              : undefined
          }
          focusTraitId={params.get('trait') as TraitId}
          traitsLoading={traitStatuses.isLoading}
          traitsError={traitStatuses.error}
          traitStatuses={traitStatuses.statuses}
          detailRequirements={requiredDetails}
          setFocusTraitId={(traitId: TraitId) => setParams({ trait: traitId })}
          onContinue={onContinue}
          onFinishLater={onFinishLater}
          ToolbarButtons={({ selectedFields }: { selectedFields?: GridRowId[] }) => (
            <Button
              onClick={() => {
                setRemoveFieldsModal({ workflowId, fieldsToRemove: selectedFields as string[] })
              }}
              disabled={!selectedFields || selectedFields.length === 0}
            >
              {t('removeSelectedFields')}
            </Button>
          )}
        />
      </Box>
    </>
  )
}

export const AddWorkflowInformation = (
  props: Pick<TraitWorkflowEditorProps, 'workflowId' | 'onContinue' | 'onFinishLater'>
) => {
  const {
    isLoading: workflowLoading,
    data: workflow,
    refetch: refetchWorkflow,
  } = useWorkflow(props.workflowId)
  if (workflowLoading) {
    return <Loading />
  }
  if (!workflow) {
    return <Navigate to={'/'} />
  }
  return <WorkflowTraitEditor {...props} workflow={workflow} refetchWorkflow={refetchWorkflow} />
}
